import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Modal, Alert, Row, Col } from 'antd';

// ROUTES
import { useRouteMatch, useHistory } from 'react-router-dom';
import { ROUTE_AAC_PESQUISA_ANTECEDENTES_PAINEL } from 'pages/iirgd/Aac/routes/paths';

// REDUX
import { useSelector, useDispatch } from 'react-redux';
import { atendimentoEncerrado } from 'store/modules/api/aac/painelDeControle/actions';
import { ApplicationState } from 'store';
import {
	ConsultaUsuarioPendente,
	UsuarioPendente,
} from 'store/modules/api/aac/novoPainelDeControle/consultaUsuarioPendente/types';
import {
	eventosPPTContabilizacaoClear,
	eventosPPTContabilizacaoRequest,
} from 'store/modules/api/utils/eventosPPT/contabilizacao/actions';
import { EventosPPTContabilizacaoRequest } from 'store/modules/api/utils/eventosPPT/contabilizacao/types';

// COMPONENTS
import Section from 'components/Common/Section';
import ButtonImage from 'components/Common/ButtonImage';
import ButtonVoltar from 'components/Common/ButtonVoltar';
import Divider from 'components/Common/Divider';

// UTILS
import { treatRequestEventsContabilizacaoPPT } from 'utils/functions/treatEventosPPTRequest';

// COMPONENTS
import { encerrarAtendimentoRequest } from 'store/modules/api/aac/novoPainelDeControle/encerrarAtendimento/actions';
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce';
import {
	emissaoAtestadosClear,
	emissaoAtestadosRequest,
} from 'store/modules/api/aac/novoPainelDeControle/emissaoAtestados/actions';
import { RequestEmissaoAtestados } from 'store/modules/api/aac/novoPainelDeControle/emissaoAtestados/types';
import PDFViewer from 'components/Common/PDFViewer';
import { baixarArquivosClear } from 'store/modules/api/utils/arquivos/baixarArquivos/actions';

import LiberacaoNormal from './LiberacaoNormal';
import LiberacaoPositiva from './LiberacaoPositiva';
import Exigencia from './Exigencia';

// STYLED
import { Content } from './styled';
import NavMenu from '../../components/NavMenu';

const PainelDeControleResultado: React.FC = () => {
	const history = useHistory();
	const dispatch = useDispatch();

	const { params }: any = useRouteMatch();
	const { id }: { id: string } = params;

	const { encerrarAtendimento } = useSelector(
		(state: ApplicationState) => state.api.aac,
	);

	const { emissaoAtestados, usuarioPendente } = useSelector(
		(state: ApplicationState) => state.api.aac,
	);

	const { baixarArquivos } = useSelector(
		(state: ApplicationState) => state.api.utils,
	);

	const { loginUnico, atendimento } = useSelector(
		(state: ApplicationState) => state.api.sgu,
	);

	const [protocoloBase64, setProtocoloBase64] = useState<{
		file: string;
		message: string;
	}>({
		file: '',
		message: '',
	});

	const [results, setResults] = useState<UsuarioPendente>({
		status: 0,
		resultado: null,
	});

	const { token } = useContext<IAuthContext>(AuthContext);

	const handleContabilizacacao = useCallback(() => {
		const payload: EventosPPTContabilizacaoRequest =
			treatRequestEventsContabilizacaoPPT(
				atendimento,
				loginUnico,
				'',
				'IIRGD',
				396,
				'atestado antecedentes',
				2,
				'iirgd_atestado_antecedentes',
			);
		dispatch(eventosPPTContabilizacaoRequest(token, payload));
	}, [atendimento, loginUnico, dispatch, token]);

	useEffect(() => {
		if (usuarioPendente.status === 200) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			setResults(usuarioPendente);
		} else if (usuarioPendente.status === 0) {
			history.push(ROUTE_AAC_PESQUISA_ANTECEDENTES_PAINEL);
		}

		if (encerrarAtendimento?.mensagem) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [encerrarAtendimento, history, usuarioPendente]);

	useEffect(() => {
		if (
			emissaoAtestados.status === 200 &&
			emissaoAtestados.data?.pdfDataUrl !== '' &&
			baixarArquivos.base64
		) {
			setProtocoloBase64({
				file: baixarArquivos.base64 || '',
				message: 'Atestado de Antecedentes',
			});
			handleContabilizacacao();
			dispatch(emissaoAtestadosClear());
		}
	}, [
		emissaoAtestados,
		dispatch,
		baixarArquivos.base64,
		handleContabilizacacao,
	]);

	const handleShutDown = useCallback(() => {
		if (results.resultado && results.resultado[Number(id)]) {
			const result = results.resultado[Number(id)];

			Modal.confirm({
				title: 'Confirma realmente o encerramento?',
				okText: 'Confirmar',
				cancelText: 'Cancelar',
				onOk() {
					dispatch(
						encerrarAtendimentoRequest(token, {
							idIdentificacao: result.id?.trim() || '',
						}),
					);
				},
				onCancel() {},
			});
		}
	}, [results.resultado, id, dispatch, token]);

	const handleServiceClosed = useCallback(
		(valueId: string, message: string) => {
			if (message) {
				dispatch(atendimentoEncerrado(valueId));
			}
		},
		[dispatch],
	);

	const getValidacaoDocumento = (valores: ConsultaUsuarioPendente) => {
		const numeroRg = valores.numeroRg?.toString().trim() || '';
		const expReg = new RegExp('[a-zA-Z]');
		const temLetra = expReg.test(numeroRg);

		if (temLetra) {
			return {
				numeroDocumento: numeroRg,
				numeroRg: '',
				digitoRg: '',
			};
		}

		return {
			numeroDocumento: '',
			numeroRg,
			digitoRg: valores.dc?.toString().trim() || '',
		};
	};

	const handlePrintLiberacaoNormal = useCallback(() => {
		if (
			results.resultado &&
			results.resultado[Number(id)].tipoLiberacao === 'N'
		) {
			const resultadoData: any = results.resultado[Number(id)];

			const docs = getValidacaoDocumento(resultadoData);

			// Construct the payload ensuring it complies with the RequestEmissaoAtestados type
			const payload: RequestEmissaoAtestados = {
				cpf: resultadoData.cpf,
				dcCpf: resultadoData.dcCpf,
				nome: resultadoData.nome,
				nomeMae: resultadoData.nomeMae,
				nomePai: resultadoData.nomePai,
				dataNascimento:
					results.resultado[Number(id)].dataNascimento?.toString(),
				sexo: resultadoData.sexo === 'M' ? 'MASCULINO' : 'FEMININO',
				resultado: resultadoData.resultado?.trim() || '',
				codigoValidacao: resultadoData.codigoValidacao?.trim() || '',
				codigoRetorno: resultadoData.retorno,
				tipoLiberacao: 'NORMAL',
				origem: 'BALCAO_UNICO',
				tipoDocApresentado: resultadoData.tipoDocApresentado,
				nomeProcurador: resultadoData.nomeProcurador,
				docProcurador: resultadoData.docProcurador,
				nomeSocial: resultadoData.nomeSocial,
				...docs,
			};

			// Dispatch the request with the constructed payload
			dispatch(emissaoAtestadosRequest(token, payload));
		}
	}, [results.resultado, id, dispatch, token]);

	const handlePrintLiberacaoPositiva = useCallback(() => {
		if (
			results.resultado &&
			results.resultado[Number(id)].tipoLiberacao === 'P'
		) {
			const camposSeNominalOuNumeral = getValidacaoDocumento(
				results.resultado[Number(id)],
			) as {
				numeroDocumento: string;
				numeroRg: string;
				digitoRg: string;
			};

			const payload = {
				nome: results.resultado[Number(id)].nome,
				nomeMae: results.resultado[Number(id)].nomeMae,
				nomePai: results.resultado[Number(id)].nomePai,
				dataNascimento:
					results.resultado[Number(id)].dataNascimento?.toString(),
				sexo:
					results.resultado[Number(id)].sexo === 'M' ? 'MASCULINO' : 'FEMININO',
				resultado: results.resultado[Number(id)].resultado?.trim() || '',
				codigoValidacao:
					results.resultado[Number(id)].codigoValidacao?.trim() || '',
				codigoRetorno: results.resultado[Number(id)].retorno,
				tipoLiberacao: 'POSITIVA',
				origem: 'BALCAO_UNICO',
				tipoDocApresentado: results.resultado[Number(id)].tipoDocApresentado,
				cpf: results.resultado[Number(id)].cpf,
				dcCpf: results.resultado[Number(id)].dcCpf,
				positivaExecucao: results.resultado[Number(id)].positivaExecucao,
				positivaInicio:
					(results.resultado[Number(id)].positivaInicio &&
						results.resultado[Number(id)].positivaInicio) ||
					'',
				positivaTermino:
					(results.resultado[Number(id)].positivaTermino &&
						results.resultado[Number(id)].positivaTermino) ||
					'',
				positivasObservacao:
					(results.resultado[Number(id)].positivasObservacao &&
						[...results.resultado[Number(id)].positivasObservacao].filter(
							Boolean,
						)) ||
					[],
				positivaRegime: results.resultado[Number(id)].positivaRegime,
				naoPgtoNumeroCertidao:
					usuarioPendente.resultado && usuarioPendente.resultado[Number(id)]
						? usuarioPendente.resultado[Number(id)].naoPgtoNumeroCertidao
						: undefined,
				naoPgtoDataCertidao:
					usuarioPendente.resultado && usuarioPendente.resultado[Number(id)]
						? usuarioPendente.resultado[Number(id)].naoPgtoDataCertidao
						: undefined,
				nomeProcurador: results.resultado[Number(id)].nomeProcurador,
				docProcurador: results.resultado[Number(id)].docProcurador,
				nomeSocial: results.resultado[Number(id)].nomeSocial,
				...camposSeNominalOuNumeral,
			};

			dispatch(emissaoAtestadosRequest(token, payload));
		}
	}, [results.resultado, id, usuarioPendente, dispatch, token]);

	const handlePrintExigencia = useCallback(() => {
		if (
			results.resultado &&
			results.resultado[Number(id)].tipoLiberacao === 'E'
		) {
			const camposSeNominalOuNumeral = getValidacaoDocumento(
				results.resultado[Number(id)],
			) as {
				numeroDocumento: string;
				numeroRg: string;
				digitoRg: string;
			};

			const payload: RequestEmissaoAtestados = {
				cpf: results.resultado[Number(id)].cpf,
				dcCpf: results.resultado[Number(id)].dcCpf,
				dataNascimento:
					results.resultado[Number(id)].dataNascimento?.toString(),
				exigenciaBO: results.resultado[Number(id)].exigenciaBO,
				exigenciaBODelegacia:
					results.resultado[Number(id)].exigenciaBODelegacia,
				exigenciaBONumero: results.resultado[Number(id)].exigenciaBONumero,
				exigenciaBaixaDesaparecido:
					results.resultado[Number(id)].exigenciaBaixaDesaparecido,
				exigenciaCertidaoCriminais:
					results.resultado[Number(id)].exigenciaCertidaoCriminais,
				exigenciasCertidaoCriminais:
					results.resultado[Number(id)].exigenciasCertidaoCriminais,
				exigenciaCertidaoDistribuidor:
					results.resultado[Number(id)].exigenciaCertidaoDistribuidor,
				exigenciasCertidaoDistribuidor:
					results.resultado[Number(id)].exigenciasCertidaoDistribuidor,
				exigenciaCertidaoObjeto:
					results.resultado[Number(id)].exigenciaCertidaoObjeto,
				exigenciaCopiaApresentado:
					results.resultado[Number(id)].exigenciaCopiaApresentado,
				exigenciasDelegacia: results.resultado[Number(id)].exigenciasDelegacia,
				exigenciasInstauracao:
					results.resultado[Number(id)].exigenciasInstauracao,
				exigenciaObservacao:
					!!results.resultado[Number(id)].exigenciaObservacao,
				exigenciasObservacao:
					results.resultado[Number(id)].exigenciasObservacao,
				exigenciasProcesso: results.resultado[Number(id)].exigenciasProcesso,
				exigenciasVara: results.resultado[Number(id)].exigenciasVara,
				exigenciasIP: results.resultado[Number(id)].exigenciasIP,
				nome: results.resultado[Number(id)].nome,
				nomeMae: results.resultado[Number(id)].nomeMae,
				nomePai: results.resultado[Number(id)].nomePai,
				origem: 'BALCAO_UNICO',
				codigoRetorno: results.resultado[Number(id)].retorno,
				codigoValidacao:
					results.resultado[Number(id)].codigoValidacao?.trim() || '',
				exigenciaEvadido: Boolean(
					results.resultado[Number(id)].exigenciaEvadido,
				),
				exigenciaPreso: Boolean(results.resultado[Number(id)].exigenciaPreso),
				exigenciaProcurado: Boolean(
					results.resultado[Number(id)].exigenciaProcurado,
				),
				id: results.resultado[Number(id)].id?.trim() || '',
				nomeSocial: results.resultado[Number(id)].nomeSocial,
				tipoDocApresentado: results.resultado[Number(id)].tipoDocApresentado,
				resultado: results.resultado[Number(id)].resultado?.trim() || '',
				retorno: results.resultado[Number(id)].retorno,
				sexo:
					results.resultado[Number(id)].sexo === 'M' ? 'MASCULINO' : 'FEMININO',
				status: results.resultado[Number(id)].status,
				tipoAA: results.resultado[Number(id)].tipoAA,
				tipoDoc: results.resultado[Number(id)].tipoDoc,
				tipoLiberacao: 'EXIGENCIA',
				usuarioCentral: results.resultado[Number(id)].usuarioCentral,
				docProcurador: results.resultado[Number(id)].docProcurador,
				nomeProcurador: results.resultado[Number(id)].nomeProcurador,
				...camposSeNominalOuNumeral,
			};

			dispatch(emissaoAtestadosRequest(token, payload));
		}
	}, [results.resultado, id, dispatch, token]);

	const handlePrint = useCallback(() => {
		Modal.confirm({
			title: 'Confirma a impressão?',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk() {
				if (results.resultado) {
					if (results.resultado[Number(id)].tipoLiberacao === 'N') {
						handlePrintLiberacaoNormal();
					} else if (results.resultado[Number(id)].tipoLiberacao === 'P') {
						handlePrintLiberacaoPositiva();
					} else if (results.resultado[Number(id)].tipoLiberacao === 'E') {
						handlePrintExigencia();
					}
				}
			},
			onCancel() {},
		});
	}, [
		results.resultado,
		id,
		handlePrintLiberacaoNormal,
		handlePrintLiberacaoPositiva,
		handlePrintExigencia,
	]);

	return (
		<>
			<Row>
				<Col span={24}>
					<NavMenu />

					<Divider mtSize={15} mbSize={15} />
				</Col>
			</Row>
			{encerrarAtendimento?.mensagem?.length > 0 && (
				<Alert
					style={{ marginBottom: '50px' }}
					description={encerrarAtendimento.mensagem}
					message=""
					type="info"
					closable
					showIcon
				/>
			)}
			<Section title="Painel de Controle">
				{results.resultado && results.resultado[Number(id)] && (
					<>
						{results.resultado[Number(id)].tipoLiberacao === 'N' && (
							<LiberacaoNormal data={results.resultado[Number(id)]} />
						)}

						{results.resultado[Number(id)].tipoLiberacao === 'P' && (
							<LiberacaoPositiva data={results.resultado[Number(id)]} />
						)}

						{results.resultado[Number(id)].tipoLiberacao === 'E' && (
							<Exigencia data={results.resultado[Number(id)]} />
						)}

						<Content>
							<ButtonImage
								type="button"
								src="retornar"
								onClick={history.goBack}
							/>

							<ButtonImage
								type="button"
								src="imprimir"
								disabled={
									!!results.resultado[Number(id)]?.exigenciaProcurado ||
									!!results.resultado[Number(id)]?.exigenciaPreso ||
									!!results.resultado[Number(id)]?.exigenciaEvadido ||
									encerrarAtendimento?.status === 200
								}
								onClick={handlePrint}
							/>

							<ButtonImage
								type="button"
								src="encerrar"
								disabled={encerrarAtendimento?.status === 200}
								onClick={handleShutDown}
							/>
						</Content>
					</>
				)}
			</Section>
			<ButtonVoltar
				onClick={() => handleServiceClosed(id, encerrarAtendimento.mensagem)}
			/>
			{protocoloBase64 && protocoloBase64.file && (
				<PDFViewer
					title={protocoloBase64.message}
					source={protocoloBase64.file}
					onClose={() => {
						setProtocoloBase64({
							file: '',
							message: '',
						});
						dispatch(baixarArquivosClear());
						dispatch(eventosPPTContabilizacaoClear());
					}}
				/>
			)}
		</>
	);
};

export default PainelDeControleResultado;
